import React, { useRef, useState } from "react";
import { api, apiErrorMessage, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";

/**
 * Single-image uploader used for the news cover photo.
 * Backend endpoint /photos/upload returns { path } which we store in value.
 */
export default function CoverPhotoUploader({ value, onChange, label = "Imagen de portada" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/photos/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = () => onChange(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <p className="text-xs text-muted-foreground mt-1">JPG, PNG o WEBP · máx 8MB · proporción recomendada 16:9</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="cover-upload-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {value ? "Cambiar" : "Subir imagen"}
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="cover-file-input"
      />
      {value ? (
        <div className="relative overflow-hidden rounded-sm border border-border group" style={{ aspectRatio: "16/9" }}>
          <img src={photoUrl(value)} alt="Portada" className="h-full w-full object-cover" data-testid="cover-preview" />
          <button
            type="button"
            onClick={remove}
            className="absolute top-2 right-2 rounded-full bg-background/90 p-1.5 hover:bg-background transition-colors"
            data-testid="cover-remove-btn"
            aria-label="Quitar imagen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 border border-dashed border-border rounded-sm text-muted-foreground" style={{ aspectRatio: "16/9" }}>
          <ImageIcon className="h-8 w-8 mb-2 opacity-60" />
          <p className="text-xs">Sin imagen de portada</p>
        </div>
      )}
    </div>
  );
}
